import React from 'react';
import { Link } from 'react-router-dom';

import SubtleBadge from 'components/common/SubtleBadge';

import paths from 'routes/paths';
import { formatStatus } from 'utils/valueFormatters';
import {
  getPurchaseOrderBadgeColor,
  getPurchaseOrderLineBadgeColor,
} from 'constants/purchaseOrder';

export const getPurchaseOrderListColumns = (tenant, warehouse) => [
  {
    accessorKey: 'po_code',
    header: 'PO Code',
    enableSorting: false,
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { id, po_code } = original;

      return (
        <Link to={paths.purchaseOrder.details(tenant, warehouse, id)}>
          <strong>{po_code}</strong>
        </Link>
      );
    },
  },
  {
    accessorKey: 'name',
    header: 'Vendor Name',
    enableSorting: false,
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { vendor } = original;

      return <span>{vendor?.name || '-'}</span>;
    },
  },
  {
    accessorKey: 'po_status',
    header: 'Status',
    enableSorting: false,
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { po_status } = original;

      return (
        <SubtleBadge pill bg={getPurchaseOrderBadgeColor(po_status)}>
          {formatStatus(po_status)}
        </SubtleBadge>
      );
    },
  },
  {
    accessorKey: 'reserved_quantity',
    header: 'Reserved Quantity',
    enableSorting: false,
    meta: {
      valueType: 'number',
    },
    cell: ({ row: { original } }) => {
      const { reserved_quantity } = original;

      return <span>{reserved_quantity}</span>;
    },
  },
  {
    accessorKey: 'reserved_weight',
    header: 'Reserved Weight',
    enableSorting: false,
    meta: {
      valueType: 'number',
    },
    cell: ({ row: { original } }) => {
      const { reserved_weight } = original;

      return (
        <span>
          {reserved_weight.value} {reserved_weight.unit}
        </span>
      );
    },
  },
  {
    accessorKey: 'total_amount',
    header: 'Total Amount',
    enableSorting: false,
    meta: {
      valueType: 'number',
    },
    cell: ({ row: { original } }) => {
      const { total_amount } = original;

      return <span>{total_amount || 0}</span>;
    },
  },
  {
    accessorKey: 'currency',
    header: 'Currency',
    enableSorting: false,
    meta: {
      meta: {
        valueType: 'string',
      },
    },
    cell: ({ row: { original } }) => {
      const { currency } = original;

      return <span>{currency || '-'}</span>;
    },
  },
  {
    accessorKey: 'created_by',
    header: 'Created By',
    enableSorting: false,
    meta: {
      meta: {
        valueType: 'string',
      },
    },
    cell: ({ row: { original } }) => {
      const { created_by } = original;

      return <span>{created_by?.email || '-'}</span>;
    },
  },
  {
    accessorKey: 'approved_by',
    header: 'Approved By',
    enableSorting: false,
    meta: {
      meta: {
        valueType: 'string',
      },
    },
    cell: ({ row: { original } }) => {
      const { approved_by } = original;

      return <span>{approved_by?.email || '-'}</span>;
    },
  },
];

export const PURCHASE_ORDER_LINE_LIST_COLUMNS = [
  {
    accessorKey: 'name',
    header: 'Name',
    enableSorting: false,
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { vendor_product } = original;

      return <span>{vendor_product?.product?.name || '-'}</span>;
    },
  },
  {
    accessorKey: 'po_line_status',
    header: 'Status',
    enableSorting: false,
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { po_line_status } = original;

      return (
        <SubtleBadge pill bg={getPurchaseOrderLineBadgeColor(po_line_status)}>
          {formatStatus(po_line_status)}
        </SubtleBadge>
      );
    },
  },
  {
    accessorKey: 'vendor_product__product__sku',
    header: 'SKU',
    enableSorting: true,
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { vendor_product } = original;

      return <span>{vendor_product?.product?.sku || '-'}</span>;
    },
  },
  {
    accessorKey: 'vendor_sku',
    header: 'Vendor SKU',
    enableSorting: false,
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { vendor_product } = original;

      return <span>{vendor_product?.vendor_sku || '-'}</span>;
    },
  },
  {
    accessorKey: 'total_quantity',
    header: 'Units',
    enableSorting: false,
    meta: {
      valueType: 'number',
    },
    cell: ({ row: { original } }) => {
      const { total_quantity } = original;

      return <span>{total_quantity || 0}</span>;
    },
  },
  {
    accessorKey: 'grn_processing_quantity',
    header: 'GRN Processing',
    enableSorting: false,
    meta: {
      valueType: 'number',
    },
    cell: ({ row: { original } }) => {
      const { grn_processing_quantity } = original;

      return <span>{grn_processing_quantity || 0}</span>;
    },
  },
  {
    accessorKey: 'put_away_quantity',
    header: 'Putaway quantity',
    enableSorting: false,
    meta: {
      valueType: 'number',
    },
    cell: ({ row: { original } }) => {
      const { put_away_quantity } = original;

      return <span>{put_away_quantity || 0}</span>;
    },
  },
  {
    accessorKey: 'unit_price',
    header: 'Unit Price',
    enableSorting: false,
    meta: {
      valueType: 'number',
    },
    cell: ({ row: { original } }) => {
      const { unit_price } = original;

      return <span>{unit_price || '-'}</span>;
    },
  },
  {
    accessorKey: 'unit_discount_price',
    header: 'Unit Discount',
    enableSorting: false,
    meta: {
      valueType: 'number',
    },
    cell: ({ row: { original } }) => {
      const { unit_discount_price } = original;

      return <span>{unit_discount_price || 0}</span>;
    },
  },
  {
    accessorKey: 'tax_type',
    header: 'Tax (%)',
    enableSorting: false,
    meta: {
      valueType: 'number',
    },
    cell: ({ row: { original } }) => {
      const { vendor_product } = original;
      const taxRate = vendor_product?.product?.tax_type?.tax_rate;

      return <span>{taxRate ? `${taxRate}` : '-'}</span>;
    },
  },
  {
    accessorKey: 'total_amount',
    header: 'Total Amount',
    enableSorting: false,
    meta: {
      valueType: 'number',
    },
    cell: ({ row: { original } }) => {
      const { unit_gross, total_quantity } = original;

      const unitGrossNum = parseFloat(unit_gross) || 0;
      const totalAmount = (unitGrossNum * total_quantity).toFixed(2);

      return <span>{totalAmount}</span>;
    },
  },
];
